import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Header } from "@/App";

/**
 * SALES PAGE — /build-your-own
 * $33 "Build Your Own: Mind Body Spirit HYGIENE™" — a guided practice.
 * One purchase delivers the printable PDF (7-day JWT link) + a permanent
 * online practice page. NO promo codes. The word "workbook" is deliberately
 * absent (reserved for the free lead magnet). No urgency/countdown/stock copy.
 */

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BRAND = {
  deep: "#042F2E",
  teal: "#068fa2",
  ocean: "#25a1c2",
  cream: "#efeae7",
  mint: "#5EEAD4",
  gold: "#F4C36A",
  goldText: "#1B6B6E",
};

const BeginButton = ({ source = "build-your-own", block = false }) => {
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const handleBuy = async () => {
    if (loading) return;
    setLoading(true);
    setErr("");
    try {
      if (typeof window !== "undefined" && typeof window.gtag === "function") {
        window.gtag("event", "begin_checkout", {
          currency: "USD",
          value: 33.0,
          items: [{ item_id: "byo_mbsh", item_name: "Build Your Own: Mind Body Spirit HYGIENE", price: 33.0, quantity: 1 }],
        });
      }
      if (typeof window !== "undefined" && typeof window.fbq === "function") {
        window.fbq("track", "InitiateCheckout", {
          value: 33.0,
          currency: "USD",
          content_ids: ["byo_mbsh"],
          content_name: "Build Your Own: Mind Body Spirit HYGIENE",
          content_type: "product",
        });
      }
      const res = await axios.post(`${API_URL}/api/pdf/checkout`, {
        origin: window.location.origin,
        source,
        product: "byo_mbsh",
      });
      if (res.data && res.data.url) {
        window.location.href = res.data.url;
      } else {
        throw new Error("No checkout URL");
      }
    } catch (e) {
      setErr("Couldn't start checkout — please try again in a moment.");
      setLoading(false);
    }
  };

  return (
    <div className={block ? "flex flex-col items-center gap-3" : "flex flex-col items-start gap-3"}>
      <button
        onClick={handleBuy}
        disabled={loading}
        className="inline-flex items-center gap-3 rounded-full font-sans font-semibold tracking-wide transition-transform hover:-translate-y-0.5 hover:shadow-2xl disabled:opacity-60"
        style={{ background: BRAND.gold, color: BRAND.goldText, fontSize: "17px", padding: "18px 40px" }}
        data-testid={block ? "byo-begin-cta-footer" : "byo-begin-cta"}
      >
        {loading ? "Opening secure checkout…" : "Begin — $33"}
        <span style={{ fontSize: "18px" }}>→</span>
      </button>
      <span className="font-sans text-xs" style={{ color: `${BRAND.cream}aa` }} data-testid="byo-begin-subcopy">
        One purchase. The printable pages and the online practice, together.
      </span>
      {err && (
        <span className="font-sans text-xs" style={{ color: "#fda4af" }} data-testid="byo-begin-error">
          {err}
        </span>
      )}
    </div>
  );
};

const BYO_FAQ = [
  {
    q: "How long does it take?",
    a: "As long as you take. A lesson is a few minutes. There are twenty-three of them. Some women do one a day, some do one a month, some do three in an afternoon and then nothing for a while. Nothing in here counts your days or tells you that you are behind.",
  },
  { q: "Do I need a printer?", a: "No. Any notebook you already have works exactly as well." },
  {
    q: "Do I need the book first?",
    text: 'No. This stands on its own. If you have read Stop Saying "I Have To." Start Living "I GET TO.", you will recognise the ground under it — but the course teaches what it needs to teach.',
    a: (
      <>
        No. This stands on its own. If you have read{" "}
        <Link
          to="/book"
          className="underline underline-offset-2 hover:opacity-80 transition-opacity"
          style={{ color: BRAND.teal }}
          data-testid="byo-faq-book-link"
        >
          Stop Saying "I Have To." Start Living "I GET TO."
        </Link>
        , you will recognise the ground under it — but the course teaches what it needs to teach.
      </>
    ),
  },
  {
    q: "What if a tool is not for me?",
    a: "Then you set it down and keep going. That is a finished lesson, the same as any other. Nothing in here asks you to save it for later or try again.",
  },
  {
    q: "Is this a live course? Will I be on camera?",
    a: "No. There is nothing to attend, nobody to meet, and no video of me. It is you, the page, and your own pen.",
  },
  {
    q: "Can I come back to it later?",
    a: "Yes — that is what the link is for. Come back after a week or after a year. Nothing will have been lost, and nothing will scold you for the gap.",
  },
];

const SITE = "https://mindbodyspirithygienetools.com";
const BYO_DESCRIPTION =
  "Twenty-three short lessons, a pen, and a practice that ends up being yours. Some of these tools will become yours. Some will not. Printable PDF + online practice. $33.";

const buildByoJsonLd = () => [
  {
    "@context": "https://schema.org",
    "@type": "Product",
    name: "Build Your Own: Mind Body Spirit HYGIENE™",
    description: BYO_DESCRIPTION,
    image: `${SITE}/share-card.png`,
    brand: { "@type": "Brand", name: "Mind Body Spirit HYGIENE™" },
    offers: {
      "@type": "Offer",
      price: "33.00",
      priceCurrency: "USD",
      availability: "https://schema.org/InStock",
      url: `${SITE}/build-your-own`,
    },
  },
  {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: BYO_FAQ.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.text || f.a },
    })),
  },
];

const BuildYourOwn = () => {
  useEffect(() => {
    const el = document.createElement("script");
    el.type = "application/ld+json";
    el.id = "byo-ld-json";
    el.textContent = JSON.stringify(buildByoJsonLd());
    document.head.appendChild(el);
    return () => el.remove();
  }, []);

  return (
    <div className="min-h-screen" style={{ background: BRAND.cream }} data-testid="build-your-own-page">
      <Header />
      {/* HERO */}
      <section
        className="px-6 pt-32 pb-20 md:pt-40 md:pb-28"
        style={{ background: `linear-gradient(160deg, ${BRAND.deep} 0%, ${BRAND.teal} 100%)`, color: BRAND.cream }}
        data-testid="byo-hero"
      >
        <div className="max-w-3xl mx-auto text-center">
          <p className="font-sans text-xs tracking-[0.4em] uppercase mb-4" style={{ color: BRAND.gold }} data-testid="byo-kicker">
            Build Your Own
          </p>
          <h1
            className="font-serif tracking-tight leading-[1.02] mb-6"
            style={{ fontSize: "clamp(2.4rem, 6vw, 4rem)" }}
            data-testid="byo-title"
          >
            Mind Body Spirit <span className="italic" style={{ color: BRAND.mint }}>HYGIENE™</span>
          </h1>
          <p className="font-serif italic text-lg md:text-xl leading-relaxed mb-4 max-w-2xl mx-auto" style={{ color: `${BRAND.cream}ee` }}>
            A guided practice — real tools for real life, to apply and integrate into your life and lifestyle.
          </p>
          <p className="font-sans text-sm tracking-wide mb-10" style={{ color: BRAND.mint }}>
            Real tools. Real women. In the messy middle.
          </p>
          <div className="flex justify-center">
            <BeginButton source="byo-hero" block />
          </div>
        </div>
      </section>

      {/* THE OPENING */}
      <section className="px-6 py-20 md:py-24" data-testid="byo-opening">
        <div className="max-w-2xl mx-auto">
          <p className="font-serif text-2xl italic mb-8" style={{ color: BRAND.teal }}>Hello Lovely,</p>
          <div className="space-y-5 font-serif text-lg leading-relaxed" style={{ color: BRAND.deep }}>
            <p>I built this the way I needed it built.</p>
            <p>
              I know what it is to run on empty — to find a program that asks for an hour a day, a schedule to keep up with,
              and a version of me who has that hour. I did not have the hour. I am guessing you have your own version of not having it.
            </p>
            <p>
              So this is the opposite of a big program. One small tool at a time, in your own time. No videos to sit through.
              No schedule. No day numbers counting what you have not done yet.
            </p>
            <p style={{ color: BRAND.teal }} className="font-medium">
              And here is the part I care about most: you are not here to copy my practice. You are here to build yours.
            </p>
            <p>
              I hand you the language and the tools. You try them. Some will become yours. Some will not. Both are a finished
              lesson — and the ones you already had before you met me go on the same shelf, with the same weight.
            </p>
          </div>
        </div>
      </section>

      {/* WHAT IT ACTUALLY IS */}
      <section className="px-6 py-20 md:py-24" style={{ background: BRAND.deep, color: BRAND.cream }} data-testid="byo-what-it-is">
        <div className="max-w-2xl mx-auto">
          <p className="font-sans text-xs tracking-[0.4em] uppercase mb-5" style={{ color: BRAND.gold }}>
            What it actually is
          </p>
          <h2 className="font-serif tracking-tight leading-[1.1] mb-8" style={{ fontSize: "clamp(1.8rem, 4vw, 2.6rem)" }}>
            Twenty-three lessons. <span className="italic" style={{ color: BRAND.mint }}>Each one short on purpose.</span>
          </h2>
          <div className="space-y-5 font-serif text-lg leading-relaxed" style={{ color: `${BRAND.cream}ee` }}>
            <p>
              You read for a few minutes. You do one small thing — right now, today, in your actual life. Then you pick up a
              pen and write one line by hand.
            </p>
            <p className="font-medium" style={{ color: BRAND.mint }}>That is a win. That is the whole method.</p>
            <p>
              The writing happens on paper, not on a screen. That is deliberate. Something moves when your hand makes the
              letters that does not move when your thumbs do. Any notebook you already have works — nobody is kept out of this by a printer.
            </p>
            <p>
              And at the end of each lesson, you decide. <em>This one is mine.</em> Or <em>not for me.</em> Or <em>here is my own version of it.</em>
            </p>
            <p>
              The ones you keep go onto your shelf. The shelf is yours — your tools, and mine, side by side. By the end, you
              are holding something nobody handed you.
            </p>
            <p>
              You are absolutely allowed to change your mind. If your life, or the way you are living it, is not working for
              you — then change your mind, and change your life. Literally. Actively. That is what these tools are for: to help
              you name what that looks like, and what it feels like. In your mind. In your body. In your spirit. And in the
              energy you give, and the energy you receive.
            </p>
          </div>
        </div>
      </section>

      {/* WHAT YOU GET */}
      <section className="px-6 py-20 md:py-24" data-testid="byo-what-you-get">
        <div className="max-w-3xl mx-auto">
          <p className="font-sans text-xs tracking-[0.4em] uppercase mb-5" style={{ color: BRAND.teal }}>
            What you get
          </p>
          <h2 className="font-serif tracking-tight mb-10" style={{ fontSize: "clamp(1.8rem, 4vw, 2.6rem)", color: BRAND.deep }}>
            One purchase. Two things, <span className="italic" style={{ color: BRAND.teal }}>and they are not sold apart.</span>
          </h2>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="p-7 rounded-2xl" style={{ background: `${BRAND.teal}11`, border: `1px solid ${BRAND.teal}22` }} data-testid="byo-get-pdf">
              <h3 className="font-serif text-2xl mb-3" style={{ color: BRAND.teal }}>The printable pages</h3>
              <p className="font-sans text-base leading-relaxed" style={{ color: `${BRAND.deep}cc` }}>
                The whole course as a PDF — instant download, yours to print, yours to keep. Put it in a binder. Write on it. Spill coffee on it.
              </p>
            </div>
            <div className="p-7 rounded-2xl" style={{ background: `${BRAND.teal}11`, border: `1px solid ${BRAND.teal}22` }} data-testid="byo-get-practice">
              <h3 className="font-serif text-2xl mb-3" style={{ color: BRAND.teal }}>The online practice</h3>
              <p className="font-sans text-base leading-relaxed" style={{ color: `${BRAND.deep}cc` }}>
                A link you bookmark and come back to for as long as you like. It remembers where you left off, holds the lines
                you write, and lets you photograph a page of your own handwriting to keep with it.
              </p>
            </div>
          </div>

          <div className="p-6 rounded-2xl font-serif italic text-lg leading-relaxed" style={{ background: `${BRAND.mint}14`, borderLeft: `3px solid ${BRAND.teal}`, color: BRAND.deep }} data-testid="byo-privacy-note">
            No accounts. No password. No app to download. Your words stay in your own browser — nothing you write and no
            photograph you take ever reaches me or anyone else.
          </div>
        </div>
      </section>

      {/* ABOUT THE PRICE */}
      <section className="px-6 py-20 md:py-24" style={{ background: `${BRAND.teal}0a` }} data-testid="byo-price">
        <div className="max-w-2xl mx-auto text-center">
          <p className="font-sans text-xs tracking-[0.4em] uppercase mb-4" style={{ color: BRAND.teal }}>About the price</p>
          <p className="font-serif tracking-tight mb-6" style={{ fontSize: "clamp(3rem, 8vw, 5rem)", color: BRAND.deep }}>$33.</p>
          <div className="space-y-4 font-serif text-lg leading-relaxed" style={{ color: BRAND.deep }}>
            <p>This is the first group going through it. The price reflects that, and it goes up when it has earned it — to $77.</p>
            <p style={{ color: `${BRAND.deep}bb` }}>
              No countdown clock. No deadline. When the price changes, it will be because the course has proven itself, not
              because a timer ran out.
            </p>
          </div>
          <div className="flex justify-center mt-10">
            <BeginButton source="byo-price" block />
          </div>
        </div>
      </section>

      {/* PLAIN ANSWERS */}
      <section className="px-6 py-20 md:py-24" data-testid="byo-faq">
        <div className="max-w-3xl mx-auto">
          <p className="font-sans text-xs tracking-[0.4em] uppercase mb-5" style={{ color: BRAND.teal }}>Plain answers</p>
          <div className="space-y-7" data-testid="byo-faq-list">
            {BYO_FAQ.map((item, idx) => (
              <div key={idx} data-testid={`byo-faq-item-${idx}`}>
                <h3 className="font-serif text-xl mb-2" style={{ color: BRAND.deep }}>{item.q}</h3>
                <p className="font-sans text-base leading-relaxed" style={{ color: `${BRAND.deep}cc` }}>{item.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* THE CLOSE */}
      <section
        className="px-6 py-20 md:py-28 text-center"
        style={{ background: `linear-gradient(180deg, ${BRAND.deep} 0%, ${BRAND.teal} 100%)`, color: BRAND.cream }}
        data-testid="byo-close"
      >
        <div className="max-w-2xl mx-auto">
          <div className="space-y-5 font-serif text-lg leading-relaxed mb-10" style={{ color: `${BRAND.cream}ee` }}>
            <p>
              You already have more than you think — things you do that help, that you have never called a practice. The
              coffee before anyone is up. The drive with the radio off. The prayer in the shower.
            </p>
            <p>This course starts there, with yours. Then it adds a few of mine, and you decide what stays.</p>
          </div>
          <div className="flex justify-center mb-10">
            <BeginButton source="byo-close" block />
          </div>
          <p className="font-serif italic text-2xl mb-1" style={{ color: BRAND.mint }}>Welcome home.</p>
          <p className="font-serif italic text-lg" style={{ color: `${BRAND.cream}cc` }}>— Temple</p>
          <p className="font-sans text-xs tracking-wide mt-6" style={{ color: BRAND.mint }}>
            Real tools. Real women. In the messy middle.
          </p>
        </div>
      </section>

    </div>
  );
};

export default BuildYourOwn;
